// Origin classifier — single source of truth used by AdminDashboard.jsx
// (row badge + detail modal) and covered by /src/__tests__/classifyOrigin.test.js .
//
// Sep 12, 2026 hardening: replaces the fuzzy "any non-Saudi country string
// means outside Riyadh" heuristic that produced a live false-positive on
// applicant 22b6c04a-… where origin_country="الرياض" (city name in the
// country field) got labeled Outside Riyadh · Outdoor Pass eligible.
//
// Rules:
//   • Both empty                                       → "unknown"
//   • Country field is a KNOWN CITY name (any country) → "review"
//        (reason "country_field_holds_city")
//   • Country valid Saudi + city Riyadh                → "riyadh" (local)
//   • Country valid Saudi + Saudi non-Riyadh city      → "outside_riyadh"
//   • Country valid non-Saudi                          → "outside_riyadh"
//   • Country non-empty but NOT in whitelist           → "review"
//        (reason "country_unrecognised")
//   • City only, no country                            → "review"
//        (reason "country_missing")
//
// The classifier is display-only — it never writes back or mutates any
// applicant field. The admin remains the sole decider of the actual pass.
import { COUNTRIES } from "./countries";
import { CITIES_BY_COUNTRY } from "./cities";

export const _norm = (s) => {
  if (s == null) return "";
  return String(s)
    // Strip bidi / RTL control marks that would otherwise slip past
    // both `hasCity` checks and the placeholder set (they render as
    // invisible characters and users often paste them by accident).
    .replace(/[\u200E\u200F\u202A-\u202E\u2066-\u2069]/g, "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
};

// Arabic normalisation: strip tashkeel (diacritics + dagger alef), collapse
// alef variants (أ إ آ → ا), fold ta-marbuta/ha, strip tatweel, strip bidi
// control chars, apply NFKC, trim + collapse whitespace.
export const _normAr = (s) => {
  if (s == null) return "";
  let raw;
  try { raw = String(s).normalize("NFKC"); } catch { raw = String(s); }
  return raw
    .replace(/[\u064B-\u065F\u0670]/g, "")           // tashkeel + dagger alef
    .replace(/\u0640/g, "")                          // tatweel (ـ)
    .replace(/[\u0622\u0623\u0625\u0671]/g, "\u0627") // alef variants → ا
    .replace(/\u0629/g, "\u0647")                    // ة → ه
    .replace(/\u0649/g, "\u064A")                    // alef maqsura → ya
    .replace(/[\u200E\u200F\u202A-\u202E\u2066-\u2069]/g, "") // bidi marks
    .trim()
    .replace(/\s+/g, " ");
};

// Authoritative country whitelist (EN + AR). Excludes "Other" which
// intentionally bypasses eligibility — such records must go to review.
const _VALID_COUNTRY_EN = new Set(
  COUNTRIES.filter((c) => c.en !== "Other").map((c) => _norm(c.en))
);
const _VALID_COUNTRY_AR = new Set(
  COUNTRIES.filter((c) => c.en !== "Other").map((c) => _normAr(c.ar))
);
const _SAUDI_COUNTRY_TOKENS = new Set([
  "saudi arabia", "saudi", "ksa", "kingdom of saudi arabia", "sa",
]);
const _SAUDI_COUNTRY_AR = new Set(
  ["المملكة العربية السعودية", "السعودية", "المملكة"].map(_normAr)
);
// Common short-form / colloquial country aliases users type. Extend
// only when a real applicant would plausibly write them. Do NOT enlarge
// beyond genuine aliases — this is not a fuzzy-match table.
const _COUNTRY_ALIAS_AR = new Map(
  [
    ["الإمارات", "United Arab Emirates"],
    ["الامارات", "United Arab Emirates"],
    ["الإمارات العربية المتحدة", "United Arab Emirates"],
    ["الامارات العربية المتحدة", "United Arab Emirates"],
    ["مصر", "Egypt"],
    ["الكويت", "Kuwait"],
    ["قطر", "Qatar"],
    ["البحرين", "Bahrain"],
    ["عمان", "Oman"],
    ["سلطنة عمان", "Oman"],
    ["الأردن", "Jordan"],
    ["الاردن", "Jordan"],
    ["تركيا", "Turkey"],
    ["أمريكا", "United States"],
    ["امريكا", "United States"],
    ["بريطانيا", "United Kingdom"],
  ].map(([k, v]) => [_normAr(k), v])
);
const _RIYADH_CITY_TOKENS = new Set([
  "riyadh", "riyad", "riadh", "arriyadh", "ar riyadh", "ar-riyadh",
]);
const _RIYADH_CITY_AR = new Set(["الرياض", "مدينة الرياض"].map(_normAr));

// Set of every known city name (EN + AR) across every country in
// CITIES_BY_COUNTRY. Used to detect the "country field carries a city
// name" case that caused the live incident.
const _KNOWN_CITY_TOKENS_ANY = (() => {
  const out = new Set();
  Object.values(CITIES_BY_COUNTRY || {}).forEach((cities) => {
    (cities || []).forEach((c) => {
      if (c && c.en) out.add(_norm(c.en));
      if (c && c.ar) out.add(_normAr(c.ar));
    });
  });
  return out;
})();

// Reverse index: cityToken → Set(countryEn). Multiple countries may share
// a city name (e.g. "Springfield") — that's fine; conflicts are only flagged
// when the applicant's country is NOT in the token's country set.
const _CITY_TO_COUNTRIES = (() => {
  const map = new Map();
  Object.entries(CITIES_BY_COUNTRY || {}).forEach(([countryEn, cities]) => {
    (cities || []).forEach((c) => {
      [c && c.en && _norm(c.en), c && c.ar && _normAr(c.ar)]
        .filter(Boolean)
        .forEach((tok) => {
          if (!map.has(tok)) map.set(tok, new Set());
          map.get(tok).add(countryEn);
        });
    });
  });
  return map;
})();

// Saudi-city token set (EN + AR) — used to distinguish "Saudi + real
// Saudi city" from "Saudi + placeholder/unknown". Manual entries not on
// this list still classify as outside_riyadh (owner directive: do NOT
// block valid manually-entered cities), unless they are placeholder
// noise or a known-city belonging to a DIFFERENT country.
const _SAUDI_CITY_TOKENS = new Set();
(CITIES_BY_COUNTRY["Saudi Arabia"] || []).forEach((c) => {
  if (c.en) _SAUDI_CITY_TOKENS.add(_norm(c.en));
  if (c.ar) _SAUDI_CITY_TOKENS.add(_normAr(c.ar));
});

// Placeholder / non-value city strings. Applicants sometimes type these
// into the city field when they don't know or don't want to answer —
// they must NOT be treated as a real "Saudi non-Riyadh" city and
// therefore MUST NOT trigger outside-Riyadh eligibility.
const _PLACEHOLDER_CITY_TOKENS = new Set([
  "unknown", "n/a", "na", "n\\a", "none", "null", "nil", "tbd", "-",
  ".", "?", "??", "???", "x", "xx", "xxx", "none.", "idk", "not sure",
  "not applicable", "not provided", "غير معروف", "لا يوجد",
]);

export function classifyOrigin(country, city) {
  const cRawEn = _norm(country);
  const ciRawEn = _norm(city);
  const cAr = _normAr(country);
  const ciAr = _normAr(city);
  const hasCountry = cRawEn.length > 0;
  const hasCity = ciRawEn.length > 0;
  if (!hasCountry && !hasCity) return { cls: "unknown", reason: "empty" };

  const isSaudi = hasCountry && (
    _SAUDI_COUNTRY_TOKENS.has(cRawEn) || _SAUDI_COUNTRY_AR.has(cAr)
  );
  // Country alias → canonical English label (for later CITIES_BY_COUNTRY lookup).
  const aliasCountryEn = _COUNTRY_ALIAS_AR.get(cAr) || null;
  const isValidCountry = hasCountry && (
    isSaudi
    || _VALID_COUNTRY_EN.has(cRawEn)
    || _VALID_COUNTRY_AR.has(cAr)
    || aliasCountryEn !== null
  );
  const isRiyadhCity = hasCity && (
    _RIYADH_CITY_TOKENS.has(ciRawEn) || _RIYADH_CITY_AR.has(ciAr)
  );
  const countryIsAKnownCity = hasCountry && (
    _KNOWN_CITY_TOKENS_ANY.has(cRawEn) || _KNOWN_CITY_TOKENS_ANY.has(cAr)
  );
  const isPlaceholderCity = hasCity && (
    _PLACEHOLDER_CITY_TOKENS.has(ciRawEn) ||
    _PLACEHOLDER_CITY_TOKENS.has(ciAr) ||
    // Punctuation-only strings (e.g. "---", "..."):
    /^[\s\-.?_/\\|]+$/.test(ciRawEn)
  );
  // The country and city agree only when the city token's set of known
  // owning countries contains the applicant's country. If the city token
  // is known to belong ONLY to different countries, that's a conflict.
  const cityOwners = _CITY_TO_COUNTRIES.get(ciRawEn) || _CITY_TO_COUNTRIES.get(ciAr) || null;
  const cityKnownForCountry = (() => {
    if (!cityOwners) return null;                // unknown token — indeterminate
    if (isSaudi && cityOwners.has("Saudi Arabia")) return true;
    // Match via English alias (e.g. AR `الإمارات` → `United Arab Emirates`).
    if (aliasCountryEn && cityOwners.has(aliasCountryEn)) return true;
    // Try matching the canonical country label (EN) — normalise both sides.
    for (const owner of cityOwners) {
      if (_norm(owner) === cRawEn) return true;
    }
    return false;
  })();

  // ---- rules ordered by owner-directed priority ----
  // (1) Country field is actually a city name.
  if (countryIsAKnownCity) return { cls: "review", reason: "country_field_holds_city" };
  // (2) City only, no country.
  if (!hasCountry && hasCity) return { cls: "review", reason: "country_missing" };
  // (3) Country present but not on the whitelist.
  if (hasCountry && !isValidCountry) return { cls: "review", reason: "country_unrecognised" };
  // (4) Riyadh city token appearing under a NON-Saudi country → conflict.
  if (isRiyadhCity && !isSaudi) return { cls: "review", reason: "city_country_conflict" };
  // (5) City is a known city that only belongs to a DIFFERENT country → conflict.
  if (hasCity && cityKnownForCountry === false) {
    return { cls: "review", reason: "city_country_conflict" };
  }
  // (6) Placeholder / noise city string → do NOT treat as a real city.
  if (hasCity && isPlaceholderCity) return { cls: "review", reason: "city_placeholder" };
  // (7) Saudi + Riyadh → local.
  if (isSaudi && isRiyadhCity) return { cls: "riyadh", reason: "saudi_riyadh" };
  // (8) Saudi + a REAL Saudi non-Riyadh city → informational outside eligibility.
  if (isSaudi && hasCity && _SAUDI_CITY_TOKENS.has(ciRawEn)) {
    return { cls: "outside_riyadh", reason: "saudi_other_city" };
  }
  if (isSaudi && hasCity && _SAUDI_CITY_TOKENS.has(ciAr)) {
    return { cls: "outside_riyadh", reason: "saudi_other_city" };
  }
  // (9) Saudi + manually-entered city not on the whitelist and not a
  //     conflict/placeholder — trust the admin's local knowledge.
  if (isSaudi && hasCity) return { cls: "outside_riyadh", reason: "saudi_other_city" };
  // (10) Saudi country without any city → ambiguous.
  if (isSaudi && !hasCity) return { cls: "review", reason: "saudi_no_city" };
  // (11) Foreign valid country WITH a city (not conflicting) → outside.
  if (isValidCountry && !isSaudi && hasCity) {
    return { cls: "outside_riyadh", reason: "international" };
  }
  // (12) Foreign valid country WITHOUT a city → incomplete.
  if (isValidCountry && !isSaudi && !hasCity) {
    return { cls: "review", reason: "foreign_country_no_city" };
  }
  return { cls: "review", reason: "fallthrough" };
}
